import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, DownloadSimple, Eraser, Pencil, Scroll, Trash, X, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";

export default function ClienteDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("prenotazioni");
  const [consentLogs, setConsentLogs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [allTags, setAllTags] = useState([]);
  const [tagPick, setTagPick] = useState("");
  const [cardLink, setCardLink] = useState(null);

  useEffect(() => { api.get("/tags").then((r) => setAllTags(r.data)).catch(() => {}); }, []);

  const reload = async () => { const r = await api.get(`/customers/${id}`); setC(r.data); };

  const startEdit = () => {
    setForm({ first_name: c.first_name || "", last_name: c.last_name || "", phone: c.phone || "",
      email: c.email || "", birth_date: c.birth_date || "", city: c.city || "",
      language: c.language || "it", sex: c.sex || "", notes: c.notes || "",
      marketing_consent: !!c.marketing_consent, blacklisted: !!c.blacklisted });
    setEditing(true);
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/customers/${id}`, form);
      await reload(); setEditing(false);
      toast.success("Cliente aggiornato");
    } catch (e) { toast.error(apiError(e)); }
  };
  const setTags = async (tags) => {
    try { await api.patch(`/customers/${id}`, { tags }); await reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addTag = () => {
    if (tagPick && !(c.tags || []).includes(tagPick)) setTags([...(c.tags || []), tagPick]);
    setTagPick("");
  };
  const removeTag = (t) => setTags((c.tags || []).filter((x) => x !== t));
  const deleteCustomer = async () => {
    if (!window.confirm(`Eliminare definitivamente ${c.first_name} ${c.last_name}? Le prenotazioni storiche restano, il contatto viene rimosso. Per l'oblio GDPR usa "Anonimizza".`)) return;
    try { await api.delete(`/customers/${id}`); toast.success("Cliente eliminato"); navigate("/clienti"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const exportGdpr = async () => {
    try {
      const r = await api.get(`/customers/${id}/gdpr-export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `gdpr-export-${(c?.first_name || "cliente").toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Export GDPR scaricato");
    } catch (e) { toast.error(apiError(e)); }
  };

  const eraseGdpr = async () => {
    if (!window.confirm("Anonimizzare definitivamente questo cliente? Nome, contatti e consensi verranno rimossi (le statistiche restano, anonime). L'operazione non e' reversibile.")) return;
    setBusy(true);
    try {
      await api.post(`/customers/${id}/gdpr-erase`);
      toast.success("Cliente anonimizzato (diritto all'oblio)");
      const r = await api.get(`/customers/${id}`);
      setC(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleLogs = async () => {
    if (consentLogs !== null) { setConsentLogs(null); return; }
    try {
      const r = await api.get(`/customers/${id}/consent-logs`);
      setConsentLogs(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id]);

  if (!c) return <div className="text-sm text-zinc-400 py-10">Caricamento...</div>;

  const tabs = [
    { key: "prenotazioni", label: `Prenotazioni (${c.reservations.length})` },
    { key: "coupon", label: `Coupon (${c.coupons.length})` },
    { key: "sondaggi", label: `Sondaggi (${c.survey_responses.length})` },
    { key: "attivita", label: "Attività" },
  ];

  return (
    <div className="space-y-4" data-testid="cliente-dettaglio">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="btn-indietro" className="p-2 rounded-md border border-zinc-200 bg-white text-zinc-600">
          <ArrowLeft size={18} />
        </button>
        <div className="w-12 h-12 rounded-full bg-zinc-900 text-white flex items-center justify-center font-display font-bold">
          {c.first_name?.[0]}{c.last_name?.[0]}
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">{c.first_name} {c.last_name}</h1>
          <p className="text-sm text-zinc-500">{c.phone} {c.email ? `· ${c.email}` : ""}</p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="gdpr-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="font-display font-bold text-sm text-zinc-900">Privacy e GDPR</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Consenso marketing: <strong>{c.marketing_consent ? "Sì" : "No"}</strong>
              {c.gdpr_erased && <span className="text-rose-600 font-semibold"> · Dati anonimizzati</span>}
            </p>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={toggleLogs} data-testid="gdpr-logs"
              className="h-8 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5 hover:bg-zinc-50">
              <Scroll size={13} weight="bold" /> Registro consensi
            </button>
            <button onClick={exportGdpr} data-testid="gdpr-export"
              className="h-8 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5 hover:bg-zinc-50">
              <DownloadSimple size={13} weight="bold" /> Export dati
            </button>
            {!c.gdpr_erased && (
              <button onClick={eraseGdpr} disabled={busy} data-testid="gdpr-erase"
                className="h-8 px-3 rounded-md bg-rose-50 border border-rose-200 text-xs font-bold text-rose-600 flex items-center gap-1.5 hover:bg-rose-100 disabled:opacity-50">
                <Eraser size={13} weight="bold" /> Anonimizza (oblio)
              </button>
            )}
          </div>
        </div>
        {consentLogs !== null && (
          <div className="mt-3 border-t border-zinc-100 pt-3 space-y-1.5" data-testid="gdpr-logs-list">
            {consentLogs.length === 0 && <p className="text-xs text-zinc-400">Nessun evento di consenso registrato.</p>}
            {consentLogs.map((l) => (
              <div key={l.id} className="flex items-center gap-2 text-xs">
                <span className={`font-bold uppercase text-[10px] px-1.5 py-0.5 rounded ${
                  l.azione === "given" ? "bg-emerald-50 text-emerald-700"
                  : l.azione === "revoked" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>
                  {l.azione === "given" ? "Dato" : l.azione === "revoked" ? "Revocato" : "Oblio"}
                </span>
                <span className="text-zinc-600">{l.dettaglio || l.fonte}</span>
                <span className="text-zinc-400 ml-auto">{(l.at || "").replace("T", " ").slice(0, 16)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { l: "Visite", v: c.visits, t: "cust-visits" },
          { l: "No-show", v: c.no_shows, t: "cust-noshows" },
          { l: "Cancellazioni", v: c.cancellations, t: "cust-cancellations" },
          { l: "Coupon usati", v: c.coupons_used, t: "cust-coupons" },
        ].map(({ l, v, t }) => (
          <div key={l} className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={t}>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">{l}</div>
            <div className="font-display text-2xl font-black text-zinc-900">{v}</div>
          </div>
        ))}
      </div>

      <section className="bg-white border border-zinc-200 rounded-lg p-4 text-sm" data-testid="cust-anagrafica">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="font-display font-bold text-zinc-900">Anagrafica e tag</p>
          <div className="flex gap-1.5">
            {editing ? (
              <>
                <button onClick={() => setEditing(false)} data-testid="cust-edit-cancel"
                  className="h-8 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700">Annulla</button>
                <button onClick={saveEdit} data-testid="cust-edit-save"
                  className="h-8 px-3 rounded-md bg-zinc-900 text-white text-xs font-bold">Salva</button>
              </>
            ) : (
              <>
                <button onClick={startEdit} data-testid="cust-edit-toggle"
                  className="h-8 px-3 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5 hover:bg-zinc-50">
                  <Pencil size={13} weight="bold" /> Modifica
                </button>
                <button onClick={deleteCustomer} data-testid="cust-delete"
                  className="h-8 px-3 rounded-md bg-rose-50 border border-rose-200 text-xs font-bold text-rose-600 flex items-center gap-1.5 hover:bg-rose-100">
                  <Trash size={13} weight="bold" /> Elimina
                </button>
              </>
            )}
          </div>
        </div>
        {editing ? (
          <div className="grid sm:grid-cols-2 gap-2" data-testid="cust-edit-form">
            {[["first_name", "Nome"], ["last_name", "Cognome"], ["phone", "Telefono"], ["email", "Email"],
              ["city", "Città"], ["notes", "Note"]].map(([k, l]) => (
              <input key={k} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                placeholder={l} data-testid={`cust-edit-${k}`}
                className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            ))}
            <input type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
              data-testid="cust-edit-birth_date"
              className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            <select value={form.sex || ""} onChange={(e) => setForm({ ...form, sex: e.target.value })} data-testid="cust-edit-sex"
              className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white">
              <option value="">Sesso —</option><option value="M">M</option><option value="F">F</option>
            </select>
            <select value={form.language || "it"} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="cust-edit-language"
              className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white">
              {["it", "en", "fr", "de", "es"].map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
            </select>
            <label className="flex items-center gap-2 text-xs text-zinc-700">
              <input type="checkbox" checked={!!form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                className="w-4 h-4 accent-zinc-900" data-testid="cust-edit-consent" /> Consenso marketing
            </label>
            <label className="flex items-center gap-2 text-xs text-zinc-700">
              <input type="checkbox" checked={!!form.blacklisted} onChange={(e) => setForm({ ...form, blacklisted: e.target.checked })}
                className="w-4 h-4 accent-rose-600" data-testid="cust-edit-blacklist" /> In blacklist
            </label>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            <div><span className="text-zinc-500">Consenso marketing:</span> <strong>{c.marketing_consent ? "Sì" : "No"}</strong></div>
            <div><span className="text-zinc-500">Lingua:</span> <strong className="uppercase">{c.language || "it"}</strong></div>
            <div><span className="text-zinc-500">Città:</span> {c.city || "—"}</div>
            <div><span className="text-zinc-500">Data di nascita:</span> {c.birth_date || "—"}</div>
            {c.sex && <div><span className="text-zinc-500">Sesso:</span> {c.sex}</div>}
            {c.source && <div><span className="text-zinc-500">Sorgente:</span> {c.source}</div>}
            {!!c.fidelity_points && <div><span className="text-zinc-500">Punti fidelity:</span> {c.fidelity_points}</div>}
            {c.last_visit && <div><span className="text-zinc-500">Ultima visita (storico):</span> {c.last_visit}</div>}
            {c.blacklisted && <div className="text-rose-600 font-semibold">In blacklist</div>}
          </div>
        )}
        <div className="sm:col-span-2 flex gap-1.5 flex-wrap mt-3 items-center" data-testid="cust-tags-row">
          {(c.tags || []).map((t) => (
            <span key={t} className="text-xs bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5 font-medium flex items-center gap-1" data-testid={`cust-tag-${t}`}>
              {t}
              <button onClick={() => removeTag(t)} data-testid={`cust-tag-del-${t}`} className="text-zinc-400 hover:text-rose-600"><X size={10} weight="bold" /></button>
            </span>
          ))}
          <select value={tagPick} onChange={(e) => setTagPick(e.target.value)} data-testid="cust-tag-pick"
            className="h-7 px-2 rounded-md border border-zinc-300 text-xs bg-white">
            <option value="">+ tag...</option>
            {allTags.filter((t) => !(c.tags || []).includes(t.name)).map((t) => (
              <option key={t.id || t.name} value={t.name}>{t.name}</option>
            ))}
          </select>
          {tagPick && (
            <button onClick={addTag} data-testid="cust-tag-add"
              className="h-7 px-2.5 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1">
              <Plus size={11} weight="bold" /> Aggiungi
            </button>
          )}
          <button onClick={async () => {
              try {
                const r = await api.get(`/fidelity/card/${c.id}`);
                setCardLink(r.data);
              } catch (e) { toast.error(apiError(e)); }
            }} data-testid="cust-fidelity-card-btn"
            className="h-7 px-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-xs font-bold">
            Card fidelity
          </button>
        </div>
        {cardLink && (
          <div className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-3" data-testid="cust-fidelity-card-panel">
            <div className="flex items-center gap-4">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/fidelity/${cardLink.url.split("/").pop()}/qr.png`}
                alt="QR fidelity card" className="w-20 h-20 rounded-md bg-white p-1" data-testid="cust-fidelity-qr" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-zinc-900">Fidelity card digitale</div>
                <p className="text-[11px] text-zinc-500 break-all">{cardLink.url}</p>
                <p className="text-[10px] text-zinc-400 mt-1">Fai inquadrare il QR al cliente, oppure inviagli il link via WhatsApp.</p>
              </div>
              <button onClick={() => setCardLink(null)} className="text-zinc-400 hover:text-zinc-700 p-1" data-testid="cust-fidelity-card-close">
                <X size={14} weight="bold" />
              </button>
            </div>
            {cardLink.just_created && cardLink.saldo > 0 && (
              <div className="rounded-md border border-amber-300 bg-white p-2.5 space-y-2" data-testid="cust-card-azzera-box">
                <p className="text-[11px] text-amber-900 font-semibold">Prima attivazione: il cliente ha già {cardLink.saldo} punti accumulati prima della card.</p>
                <div className="flex gap-2">
                  <button onClick={() => setCardLink({ ...cardLink, just_created: false })} data-testid="cust-card-mantieni"
                    className="flex-1 h-8 rounded-md border border-zinc-300 text-zinc-700 text-[11px] font-bold">Mantieni {cardLink.saldo} punti</button>
                  <button onClick={async () => {
                      try {
                        await api.post("/fidelity/movimento", {
                          customer_id: c.id, punti: -cardLink.saldo,
                          motivo: "Azzeramento punti pregressi (attivazione card)",
                        });
                        toast.success("Punti azzerati: la card parte da zero");
                        setCardLink({ ...cardLink, saldo: 0, just_created: false });
                      } catch (e) { toast.error(apiError(e)); }
                    }} data-testid="cust-card-azzera"
                    className="flex-1 h-8 rounded-md bg-rose-600 text-white text-[11px] font-bold hover:bg-rose-700">Azzera: parti da 0</button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`shrink-0 h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
              tab === t.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "prenotazioni" && (
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="cust-reservations">
          {c.reservations.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessuna prenotazione</div>}
          {c.reservations.map((r) => (
            <button key={r.id} onClick={() => navigate(`/prenotazioni/${r.id}`)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 text-left">
              <span className="text-sm font-medium text-zinc-900 w-24">{r.date}</span>
              <span className="font-display font-bold text-zinc-900">{r.time}</span>
              <span className="text-xs text-zinc-500 flex-1">{r.party_size} pers. {r.coupon_name ? `· ${r.coupon_name}` : ""}</span>
              <StatusBadge status={r.status} />
            </button>
          ))}
        </div>
      )}
      {tab === "coupon" && (
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="cust-coupons-list">
          {c.coupons.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun coupon</div>}
          {c.coupons.map((cc) => (
            <div key={cc.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-zinc-900">{cc.coupon_name}</div>
                <div className="text-xs text-zinc-500 font-mono">{cc.code}</div>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                cc.status === "utilizzato" ? "bg-zinc-100 text-zinc-600 border-zinc-300"
                : cc.status === "attivo" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : "bg-rose-100 text-rose-700 border-rose-300"}`}>{cc.status}</span>
            </div>
          ))}
        </div>
      )}
      {tab === "sondaggi" && (
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="cust-surveys-list">
          {c.survey_responses.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessun sondaggio compilato</div>}
          {c.survey_responses.map((r) => (
            <div key={r.id} className="px-4 py-3">
              <div className="text-sm font-semibold text-zinc-900">{r.survey_name}</div>
              <div className="text-xs text-zinc-500 mb-1">{new Date(r.submitted_at).toLocaleDateString("it-IT")}</div>
              {r.answers.map((a, i) => (
                <div key={i} className="text-xs text-zinc-600">· {a.question}: <strong>{a.value}</strong></div>
              ))}
            </div>
          ))}
        </div>
      )}
      {tab === "attivita" && (
        <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="cust-activities">
          {c.activities.length === 0 && <div className="py-8 text-center text-sm text-zinc-400">Nessuna attività</div>}
          {c.activities.map((a) => (
            <div key={a.id} className="px-4 py-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-zinc-900">{a.automation_name || a.action}</span>
                <span className="text-xs text-zinc-400">{a.channel}</span>
              </div>
              <div className="text-xs text-zinc-500">{a.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
